import React, { useState } from "react";
import { upload } from "@vercel/blob/client";
import { useEtatGlobal } from "../EtatGlobal";
import { authFetch } from '../../HttpClient';
import {
  AlertCircle,
  FileText,
  ChevronUp,
  ChevronDown,
  Loader2,
  Check,
  X,
  Headphones,
} from "lucide-react";

// La « Création client » a été retirée du ticketing (09/10/2026) : plus
// personne ne crée de client depuis un ticket.
// Catégories du type "informatique" uniquement (Ticketing.categorie, enum
// CategorieTicket côté Prisma) — le type MyAlfa n'a pas de catégorie.
const TICKET_CATEGORIES = [
  { value: "IMPRESSION", label: "Impression" },
  { value: "FENETRE", label: "Fenêtre bloquée" },
  { value: "ACD", label: "Connexion logiciels ACD" },
  { value: "MATERIEL", label: "Matériel" },
  { value: "AUTRES", label: "Autres" },
];

function TicketingForm({ onCancel }) {
  const [formData, setFormData] = useState({
    title: "",
    type: " ",
    categorie: "AUTRES",
    description: "",
    nomUser: "",
    email: "",
    status: "En cours",
    priority: " ",
    document: null, // Ajout du document
  });

  console.log(formData);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [ChaqueAssocieDocument, setChaqueAssocieDocument] = useState(false);

  const { utilisateur } = useEtatGlobal();

  const isInformatique = formData.type === "informatique";

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "Document") {
      setFormData({ ...formData, document: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const submitGenericTicket = async () => {
    // ---- Test upload direct navigateur → Vercel Blob (23/09/2026) ----
    // Contourne la limite de 4,5 Mo des Vercel Functions : le fichier part
    // directement du navigateur vers Blob, le serveur ne reçoit que l'URL.
    // Auth via clientPayload (le JWT) : upload() ne sait pas envoyer
    // l'en-tête Authorization, voir handleTicketUploadToken côté backend.
    let documentUrl = null;
    if (formData.document) {
      const blob = await upload(formData.document.name, formData.document, {
        access: "public",
        handleUploadUrl: "https://backend-myalfa.vercel.app/api/ticketing/upload-token",
        clientPayload: localStorage.getItem("token"),
      });
      documentUrl = blob.url;
    }

    const responseTicket = await authFetch("https://backend-myalfa.vercel.app/api/ticketing", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: formData.title,
        type: formData.type,
        categorie: isInformatique ? formData.categorie : null,
        description: formData.description,
        nomUser: `${utilisateur.lastName} ${utilisateur.name}`,
        email: utilisateur.email,
        status: "En cours",
        priority: formData.priority,
        documentUrl,
      }),
    });

    if (!responseTicket.ok) {
      throw new Error("Erreur lors de l'envoi du ticket.");
    }

    // Récupère l'URL du document uploadé (Vercel Blob) pour le joindre au mail
    const createdTicket = await responseTicket.json();

    // ---- Envoi en parallèle vers l'API mail-ticket-create ----
    const responseMail = await authFetch("https://backend-myalfa.vercel.app/api/mail-ticket-create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: formData.title,
        type: formData.type,
        description: formData.description,
        nomUser: `${utilisateur.lastName} ${utilisateur.name}`,
        email: utilisateur.email,
        priority: formData.priority,
        documentUrl: createdTicket.Document || null,
      }),
    });

    if (!responseMail.ok) {
      throw new Error("Erreur lors de l'envoi de l'email de notification.");
    }

    setMessage("✅ Ticket envoyé avec succès !");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      await submitGenericTicket();

      setFormData({
        title: "",
        type: " ",
        categorie: "AUTRES",
        description: "",
        nomUser: "",
        email: "",
        status: "En cours",
        priority: " ",
        document: null,
      });
    } catch (error) {
      console.error(error);
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
      onCancel();
    }
  };
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pt-4 pb-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#9B1B5D] flex items-center justify-center">
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              Demande d'assistance IT
            </h2>
            <p className="text-xs text-slate-400">Créer un ticket de support</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Corps du formulaire */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

        {/* ── Type de demande ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <AlertCircle className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">Informations essentielles</span>
          </div>
          <div className="p-4">
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Votre demande <span className="text-red-400">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
            >
              <option value="">— Sélectionner le type de support —</option>
              <option value="MyAlfa">MyAlfa WorkSync</option>
              <option value="informatique">Informatique</option>
            </select>
          </div>
        </div>

            {/* ── Détails du ticket ── */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <FileText className="w-4 h-4 text-[#9B1B5D]" />
                <span className="text-sm font-semibold text-slate-700">Détails du ticket</span>
              </div>
              <div className="p-4 space-y-3">
                <div className={isInformatique ? "grid gap-3" : ""} style={isInformatique ? { gridTemplateColumns: "160px 1fr" } : undefined}>
                  {isInformatique && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Catégorie</label>
                      <select
                        name="categorie"
                        value={formData.categorie}
                        onChange={handleChange}
                        className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                      >
                        {TICKET_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Titre</label>
                    <input
                      name="title"
                      type="text"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Demande de..."
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Priorité</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                  >
                    <option value=" ">— Sélectionner la priorité —</option>
                    <option value="Classique">Classique</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                  <textarea
                    name="description"
                    placeholder="Décrivez votre problème..."
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all resize-none"
                  />
                </div>
              </div>
            </div>

            {/* ── Document joint ── */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setChaqueAssocieDocument(!ChaqueAssocieDocument)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#9B1B5D]" />
                  <span className="text-sm font-semibold text-slate-700">Document joint</span>
                  <span className="text-xs text-slate-400 italic">(optionnel)</span>
                </div>
                {ChaqueAssocieDocument ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {ChaqueAssocieDocument && (
                <div className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs text-slate-600 shrink-0">Fichier</span>
                    <input
                      type="file"
                      name="Document"
                      onChange={handleChange}
                      accept=".docx,.pdf,.png,.jpg,.jpeg,.webp,.heic,.xls,.xlsx"
                      className="text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#9B1B5D]/10 file:text-[#9B1B5D] hover:file:bg-[#9B1B5D]/20 cursor-pointer"
                    />
                  </div>
                </div>
              )}
            </div>

        {message && <p className="text-xs text-center text-slate-500">{message}</p>}
      </form>

      {/* Pied de page fixe */}
      <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 h-10 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          onClick={handleSubmit}
          className="flex items-center gap-2 px-8 h-10 bg-[#9B1B5D] hover:bg-[#7A1548] text-white text-sm font-medium rounded-lg shadow-sm shadow-[#9B1B5D]/20 transition-colors disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Création en cours...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Créer le ticket
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default TicketingForm;
